import { useState, useEffect } from "react";
import { api, uploadAndProcess } from "../lib/api";
import { Sparkles, FileSearch, BookMarked, Upload, Check, AlertTriangle, Waves, ArrowRight, X as XIcon } from "lucide-react";

const STYLES = ["APA", "MLA", "Harvard", "Chicago"];
const TYPE_COLOURS = {
    spelling: "var(--status-red)",
    grammar: "var(--brand-magenta)",
    style: "var(--brand-purple)",
    clarity: "var(--brand-orange)",
};
const CATEGORY_META = {
    cohesion: { label: "Cohesion", colour: "var(--brand-purple)" },
    readability: { label: "Readability", colour: "var(--brand-orange)" },
    sentence_variety: { label: "Sentence Variety", colour: "var(--brand-magenta)" },
    academic_tone: { label: "Academic Tone", colour: "var(--status-green)" },
    paragraph_structure: { label: "Paragraph Structure", colour: "#8e24aa" },
};

export default function AssessmentTools({ projectId }) {
    const [tab, setTab] = useState("proofread");
    return (
        <div className="card-warm p-5" data-testid="assessment-tools">
            <div className="font-serif text-2xl mb-2 flex items-center gap-2">
                <Sparkles size={18} className="text-[var(--brand-magenta)]" /> Assessment Tools
            </div>
            <div className="flex gap-2 mb-4 flex-wrap">
                <button onClick={() => setTab("proofread")} data-testid="tools-tab-proofread"
                        className={`text-xs px-3 py-1.5 rounded-full ${tab === "proofread" ? "brand-gradient text-white" : "btn-ghost"}`}>
                    Spelling & Grammar
                </button>
                <button onClick={() => setTab("flow")} data-testid="tools-tab-flow"
                        className={`text-xs px-3 py-1.5 rounded-full ${tab === "flow" ? "brand-gradient text-white" : "btn-ghost"}`}>
                    Improve Flow
                </button>
                <button onClick={() => setTab("guide")} data-testid="tools-tab-guide"
                        className={`text-xs px-3 py-1.5 rounded-full ${tab === "guide" ? "brand-gradient text-white" : "btn-ghost"}`}>
                    Reference Guide
                </button>
            </div>
            {tab === "proofread" && <Proofread />}
            {tab === "flow" && <ImproveFlow projectId={projectId} />}
            {tab === "guide" && <ReferenceGuide />}
        </div>
    );
}

function Proofread() {
    const [text, setText] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [stage, setStage] = useState(""); // "" | "uploading" | "processing" | "complete"
    const [uploadPct, setUploadPct] = useState(0);
    const [filename, setFilename] = useState("");
    const [error, setError] = useState("");

    const onFile = async (e) => {
        const f = e.target.files[0]; if (!f) return;
        setError(""); setExtracting(true); setFilename(f.name);
        setStage("uploading"); setUploadPct(0);
        try {
            const data = await uploadAndProcess({
                files: f,
                kind: "document_extract",
                onStatus: (s, info) => {
                    setStage(s);
                    if (s === "uploading" && typeof info?.percent === "number") {
                        setUploadPct(info.percent);
                    }
                },
            });
            setText(data.text || data.combined_text || "");
            setStage("complete");
        } catch (er) {
            setError(er.friendly || er.response?.data?.detail || er.message);
            setStage("");
        } finally { setExtracting(false); e.target.value = ""; }
    };

    const run = async () => {
        setError(""); setLoading(true); setResult(null);
        try {
            const { data } = await api.post("/ai/proofread", { text });
            setResult(data);
        } catch (er) {
            setError(er.response?.data?.detail || er.message);
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="tools-proofread">
            <div className="flex flex-wrap gap-2 mb-2">
                <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-1" data-testid="proofread-upload">
                    <Upload size={12} /> Upload .pdf / .docx / .txt
                    <input type="file" accept=".pdf,.docx,.txt" onChange={onFile} className="hidden" />
                </label>
                {filename && <span className="text-xs text-[var(--text-ink)]/55">{filename}</span>}
                {stage === "uploading" && (
                    <span className="text-xs" data-testid="proofread-stage-uploading">
                        Uploading… {uploadPct}%
                    </span>
                )}
                {stage === "processing" && (
                    <span className="text-xs inline-flex items-center gap-1" data-testid="proofread-stage-processing">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-orange)] animate-pulse" />
                        Processing document…
                    </span>
                )}
                {stage === "complete" && (
                    <span className="text-xs text-[var(--status-green)]" data-testid="proofread-stage-complete">
                        Complete ✓
                    </span>
                )}
            </div>
            {extracting && (
                <div className="h-1 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden mb-2" data-testid="proofread-progress-bar">
                    <div
                        className="h-full brand-gradient transition-all duration-300"
                        style={{
                            width: stage === "uploading"
                                ? `${Math.max(5, uploadPct)}%`
                                : stage === "processing"
                                    ? "100%"
                                    : "0%",
                            opacity: stage === "processing" ? 0.6 : 1,
                        }}
                    />
                </div>
            )}
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                placeholder="Paste your draft, essay, or report here…"
                data-testid="proofread-input"
                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm"
            />
            <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[var(--text-ink)]/55">{text.length.toLocaleString()} chars</span>
                <button onClick={run} disabled={loading || !text.trim()}
                        data-testid="proofread-run-btn"
                        className="btn-primary text-sm inline-flex items-center gap-2">
                    <FileSearch size={14} /> {loading ? "Reading…" : "Proofread"}
                </button>
            </div>
            {error && <div className="text-sm text-[var(--status-red)] mt-3">{error}</div>}
            {result && (
                <div className="mt-4 space-y-3" data-testid="proofread-result">
                    <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--brand-purple)" }}>
                        <div className="flex items-center justify-between">
                            <div className="font-script text-lg text-[var(--brand-orange)]">overall</div>
                            {result.score !== null && result.score !== undefined && (
                                <div className="font-serif text-2xl" style={{ color: result.score >= 80 ? "var(--status-green)" : result.score >= 60 ? "var(--brand-orange)" : "var(--status-red)" }}>
                                    {result.score}/100
                                </div>
                            )}
                        </div>
                        <div className="text-sm text-[var(--text-ink)]/75">{result.summary}</div>
                    </div>
                    {Array.isArray(result.issues) && result.issues.length > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55 mb-2">{result.issues.length} suggestion{result.issues.length === 1 ? "" : "s"}</div>
                            <ul className="space-y-2">
                                {result.issues.map((iss, i) => (
                                    <li key={i} className="card-warm p-3 text-sm" data-testid={`proofread-issue-${i}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <AlertTriangle size={12} style={{ color: TYPE_COLOURS[iss.type] || "var(--brand-magenta)" }} />
                                            <span className="text-[10px] uppercase tracking-wide" style={{ color: TYPE_COLOURS[iss.type] || "var(--brand-magenta)" }}>{iss.type}</span>
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-2">
                                            <div><span className="text-[10px] uppercase text-[var(--text-ink)]/55">Original</span>
                                                <div className="line-through text-[var(--text-ink)]/70">{iss.original}</div></div>
                                            <div><span className="text-[10px] uppercase text-[var(--text-ink)]/55">Suggestion</span>
                                                <div className="text-[var(--status-green)]">{iss.suggestion}</div></div>
                                        </div>
                                        {iss.explanation && <div className="text-xs text-[var(--text-ink)]/60 mt-1.5">{iss.explanation}</div>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {result.corrected && (
                        <div>
                            <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">Corrected version</div>
                            <textarea
                                value={result.corrected} readOnly rows={6}
                                data-testid="proofread-corrected"
                                className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm bg-[var(--bg-cream-2)]/40"
                            />
                            <button onClick={() => { navigator.clipboard?.writeText(result.corrected); }}
                                    className="btn-ghost text-xs mt-2 inline-flex items-center gap-1">
                                <Check size={12} /> Copy corrected text
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ReferenceGuide() {
    const [style, setStyle] = useState("Harvard");
    const [guide, setGuide] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        setError(""); setLoading(true); setGuide(null);
        try {
            const { data } = await api.post("/ai/reference-guide", { style });
            setGuide(data);
        } catch (er) {
            setError(er.response?.data?.detail || er.message);
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="tools-reference-guide">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="guide-style"
                        className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    {STYLES.map(s => <option key={s}>{s}</option>)}
                </select>
                <button onClick={load} disabled={loading}
                        data-testid="guide-build-btn"
                        className="btn-primary text-sm inline-flex items-center gap-2">
                    <BookMarked size={14} /> {loading ? "Building…" : "Build my reference guide"}
                </button>
            </div>
            {error && <div className="text-sm text-[var(--status-red)] mb-2">{error}</div>}
            {guide && (
                <div className="space-y-3" data-testid="guide-result">
                    <div className="card-warm p-4">
                        <div className="font-script text-lg text-[var(--brand-orange)]">{guide.style}</div>
                        <div className="text-sm text-[var(--text-ink)]/75 mt-1">{guide.intro}</div>
                    </div>
                    {Array.isArray(guide.in_text_rules) && guide.in_text_rules.length > 0 && (
                        <div className="card-warm p-4">
                            <div className="font-serif text-lg mb-2">In-text citation rules</div>
                            <ul className="text-sm space-y-1 list-disc pl-4">
                                {guide.in_text_rules.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                        </div>
                    )}
                    {Array.isArray(guide.examples) && guide.examples.length > 0 && (
                        <div className="grid sm:grid-cols-2 gap-2">
                            {guide.examples.map((ex, i) => (
                                <div key={i} className="card-warm p-3">
                                    <div className="text-[10px] uppercase tracking-wide text-[var(--brand-purple)]">{ex.type}</div>
                                    {ex.template && <div className="text-xs text-[var(--text-ink)]/60 mt-1 font-mono">{ex.template}</div>}
                                    {ex.example && <div className="text-sm mt-2">{ex.example}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                    {Array.isArray(guide.common_mistakes) && guide.common_mistakes.length > 0 && (
                        <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--status-red)" }}>
                            <div className="font-serif text-lg mb-2">Common mistakes to avoid</div>
                            <ul className="text-sm space-y-1 list-disc pl-4">
                                {guide.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ImproveFlow({ projectId }) {
    const [text, setText] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [dismissed, setDismissed] = useState({});
    const [applied, setApplied] = useState({});
    const [snapshots, setSnapshots] = useState([]);

    useEffect(() => {
        if (!projectId) return;
        api.get("/flow_snapshots")
            .then(r => setSnapshots(r.data.filter(s => s.project_id === projectId).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))))
            .catch(() => {});
    }, [projectId]);

    const run = async () => {
        setError(""); setLoading(true); setResult(null);
        setDismissed({}); setApplied({});
        try {
            const { data } = await api.post("/ai/improve-flow", { text });
            setResult(data);
            if (projectId && data && (data.score !== null && data.score !== undefined)) {
                try {
                    const { data: snap } = await api.post("/flow_snapshots", {
                        project_id: projectId,
                        score: data.score,
                        subscores: data.subscores || {},
                        chars: text.length,
                        suggestion_count: (data.suggestions || []).length,
                    });
                    setSnapshots(s => [...s, snap]);
                } catch (_) {
                    /* snapshot save is best-effort */
                }
            }
        } catch (er) {
            setError(er.response?.data?.detail || er.message);
        } finally { setLoading(false); }
    };

    const dismiss = (i) => setDismissed(d => ({ ...d, [i]: true }));
    const markApplied = (i) => setApplied(a => ({ ...a, [i]: true }));
    const removeSnapshot = async (id) => {
        if (!window.confirm("Remove this snapshot from history?")) return;
        await api.delete(`/flow_snapshots/${id}`);
        setSnapshots(s => s.filter(x => x.id !== id));
    };

    const subs = result?.subscores || {};
    const visibleSuggestions = (result?.suggestions || [])
        .map((s, i) => ({ ...s, _i: i }))
        .filter(s => !dismissed[s._i]);

    const grouped = visibleSuggestions.reduce((acc, s) => {
        const k = s.category || "cohesion";
        (acc[k] = acc[k] || []).push(s);
        return acc;
    }, {});

    const scoreColour = (n) => n >= 80 ? "var(--status-green)" : n >= 60 ? "var(--brand-orange)" : "var(--status-red)";

    return (
        <div data-testid="tools-flow">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
                      placeholder="Paste your draft — we'll coach the flow, not rewrite it…"
                      data-testid="flow-input"
                      className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[var(--text-ink)]/55">{text.length.toLocaleString()} chars · your text stays unchanged</span>
                <button onClick={run} disabled={loading || !text.trim()}
                        data-testid="flow-run-btn"
                        className="btn-primary text-sm inline-flex items-center gap-2">
                    <Waves size={14} /> {loading ? "Analysing…" : "Analyse flow"}
                </button>
            </div>
            {snapshots.length > 0 && <SnapshotHistory snapshots={snapshots} onRemove={removeSnapshot} />}
            {error && <div className="text-sm text-[var(--status-red)] mt-3">{error}</div>}
            {result && (
                <div className="mt-4 space-y-3" data-testid="flow-result">
                    <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: scoreColour(result.score || 0) }}>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <div className="font-script text-lg text-[var(--brand-orange)]">academic flow score</div>
                                <div className="text-sm text-[var(--text-ink)]/75 mt-0.5">{result.summary}</div>
                            </div>
                            {result.score !== null && result.score !== undefined && (
                                <div className="font-serif text-4xl" style={{ color: scoreColour(result.score) }}>
                                    {result.score}<span className="text-base text-[var(--text-ink)]/45">/100</span>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2" data-testid="flow-subscores">
                            {Object.entries(CATEGORY_META).map(([key, meta]) => {
                                const val = subs[key] ?? 0;
                                return (
                                    <div key={key} className="">
                                        <div className="flex justify-between text-[10px] uppercase tracking-wide mb-1">
                                            <span style={{ color: meta.colour }}>{meta.label}</span>
                                            <span className="text-[var(--text-ink)]/55">{val}</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                                            <div className="h-full transition-all" style={{ width: `${val}%`, background: meta.colour }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Original text (read-only, always visible) */}
                    <div>
                        <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">Your text · unchanged</div>
                        <div className="card-warm p-3 max-h-40 overflow-auto scrollbar-thin text-sm whitespace-pre-wrap" data-testid="flow-original">
                            {text}
                        </div>
                    </div>

                    {/* Grouped suggestions */}
                    {Object.keys(grouped).length === 0 ? (
                        <div className="card-warm p-4 text-sm text-[var(--text-ink)]/65 flex items-center gap-2">
                            <Check size={14} className="text-[var(--status-green)]" />
                            All suggestions handled. Your text is reading clean.
                        </div>
                    ) : (
                        Object.entries(CATEGORY_META).map(([key, meta]) => {
                            const list = grouped[key]; if (!list || list.length === 0) return null;
                            return (
                                <div key={key} data-testid={`flow-group-${key}`}>
                                    <div className="text-xs uppercase tracking-wide mb-1.5 flex items-center gap-2">
                                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: meta.colour }} />
                                        <span style={{ color: meta.colour }}>{meta.label}</span>
                                        <span className="text-[var(--text-ink)]/50">· {list.length}</span>
                                    </div>
                                    <ul className="space-y-2">
                                        {list.map(s => (
                                            <li key={s._i} className={`card-warm p-3 text-sm ${applied[s._i] ? "opacity-60" : ""}`}
                                                data-testid={`flow-suggestion-${s._i}`}>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        {s.original_excerpt && (
                                                            <div className="mb-1.5">
                                                                <span className="text-[10px] uppercase text-[var(--text-ink)]/55">Refers to</span>
                                                                <div className="italic text-[var(--text-ink)]/70 bg-[var(--bg-cream-2)]/50 rounded px-2 py-1 mt-0.5 line-clamp-2">
                                                                    “{s.original_excerpt}”
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="flex items-start gap-2">
                                                            <ArrowRight size={14} className="mt-0.5 shrink-0" style={{ color: meta.colour }} />
                                                            <div>
                                                                <div className="text-[var(--text-ink)]">{s.recommendation}</div>
                                                                {s.why && <div className="text-xs text-[var(--text-ink)]/55 mt-1">{s.why}</div>}
                                                                {s.type && (
                                                                    <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: `${meta.colour}22`, color: meta.colour }}>
                                                                        {s.type.replace("_", " ")}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1 shrink-0">
                                                        {!applied[s._i] && (
                                                            <button onClick={() => markApplied(s._i)}
                                                                    data-testid={`flow-apply-${s._i}`}
                                                                    title="I'll apply this"
                                                                    className="p-1.5 rounded-full hover:bg-[var(--status-green)]/15 text-[var(--status-green)]">
                                                                <Check size={14} />
                                                            </button>
                                                        )}
                                                        <button onClick={() => dismiss(s._i)}
                                                                data-testid={`flow-dismiss-${s._i}`}
                                                                title="Dismiss"
                                                                className="p-1.5 rounded-full hover:bg-[rgba(229,57,53,0.12)] text-[var(--text-ink)]/55">
                                                            <XIcon size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })
                    )}

                    <div className="text-[11px] text-[var(--text-ink)]/50 mt-2 flex items-start gap-1.5">
                        <Sparkles size={12} className="mt-0.5" />
                        Suggestions are recommendations — your writing remains your own. Nothing has been changed.
                    </div>
                </div>
            )}
        </div>
    );
}


function SnapshotHistory({ snapshots, onRemove }) {
    const sorted = [...snapshots].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const scores = sorted.map(s => Math.max(0, Math.min(100, parseFloat(s.score) || 0)));
    const w = 560, h = 80, padX = 14, padY = 12;
    const first = scores[0];
    const last = scores[scores.length - 1];
    const delta = scores.length >= 2 ? Math.round(last - first) : 0;
    const max = 100;
    const points = scores.map((v, i) => {
        const x = scores.length === 1 ? w / 2 : padX + (i * (w - padX * 2)) / (scores.length - 1);
        const y = h - padY - (v / max) * (h - padY * 2);
        return [x, y];
    });
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
    return (
        <div className="card-warm p-4 mt-3" data-testid="flow-history">
            <div className="flex items-baseline justify-between mb-2">
                <div>
                    <div className="font-script text-[var(--brand-orange)]">progress chart</div>
                    <div className="font-serif text-lg">Your own work, just stronger</div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55">Latest</div>
                    <div className="font-serif text-2xl" style={{ color: last >= 80 ? "var(--status-green)" : last >= 60 ? "var(--brand-orange)" : "var(--status-red)" }}>{last}/100</div>
                    {scores.length >= 2 && (
                        <div className="text-xs" style={{ color: delta > 0 ? "var(--status-green)" : delta < 0 ? "var(--status-red)" : "var(--text-ink)" }}>
                            {delta > 0 ? "▲" : delta < 0 ? "▼" : "·"} {Math.abs(delta)} from first
                        </div>
                    )}
                </div>
            </div>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="vu-flow-line" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#7B2D8E" />
                        <stop offset="50%" stopColor="#E91E63" />
                        <stop offset="100%" stopColor="#FF6B35" />
                    </linearGradient>
                </defs>
                {/* baseline grid lines */}
                {[20, 40, 60, 80].map(v => {
                    const y = h - padY - (v / max) * (h - padY * 2);
                    return <line key={v} x1={padX} x2={w - padX} y1={y} y2={y} stroke="rgba(26,19,24,0.06)" strokeDasharray="2 4" />;
                })}
                {scores.length >= 2 && (
                    <path d={path} fill="none" stroke="url(#vu-flow-line)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                )}
                {points.map((p, i) => (
                    <g key={i}>
                        <circle cx={p[0]} cy={p[1]} r="4" fill="#fff" stroke="#7B2D8E" strokeWidth="2" />
                        <text x={p[0]} y={p[1] - 8} textAnchor="middle" fontSize="10" fill="#1A1318">{scores[i]}</text>
                    </g>
                ))}
            </svg>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
                {sorted.slice(-8).reverse().map(s => (
                    <div key={s.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded-lg hover:bg-[rgba(255,107,53,0.08)] group" data-testid={`flow-snapshot-${s.id}`}>
                        <span className="text-[var(--text-ink)]/65">{new Date(s.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} {new Date(s.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                        <span className="font-mono font-semibold" style={{ color: s.score >= 80 ? "var(--status-green)" : s.score >= 60 ? "var(--brand-orange)" : "var(--status-red)" }}>{s.score}</span>
                        <button onClick={() => onRemove(s.id)} title="Remove" className="opacity-0 group-hover:opacity-100 text-[var(--text-ink)]/55 hover:text-[var(--status-red)]">×</button>
                    </div>
                ))}
            </div>
        </div>
    );
}

